/**
 * TopicPickerMulti — checkbox multi-select variant of TopicPicker
 * (iter 105.2).
 *
 * Design (per user + agent alignment):
 *   • Same indent-tree browse + searchable breadcrumb list as
 *     <TopicPicker>, but each row has a checkbox instead of
 *     select-and-close.
 *   • Selected topics render as breadcrumb chips inside the trigger
 *     button and above the tree, each with an × to remove.
 *   • Redundancy hint: when a user selects a topic whose ancestor is
 *     also selected (or vice versa), a yellow inline banner appears
 *     at the top of the dropdown with a "Fix" one-click button that
 *     removes the redundant ancestor(s), keeping only the deepest
 *     nodes on each path. Advisory-only — no silent auto-optimise.
 *     Rationale: the backend expands descendants at query time, so
 *     tagging `Rules > SOLAS > LSA` all together is identical in
 *     effect to tagging just `LSA`. Guiding admins toward minimal
 *     tags reduces `topic_ids` bloat and downstream rollup confusion.
 *
 * Usage:
 *   <TopicPickerMulti
 *     topics={topics}                    // flat array
 *     value={topicIds}                   // array of selected ids
 *     onChange={setTopicIds}
 *     placeholder="Tag topics…"
 *     testIdPrefix="question-tag"
 *   />
 */
import { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronDown, Search, X, Check, AlertTriangle, ArrowLeft, FolderTree } from 'lucide-react';
import { countDescendants, getSubtreeIds } from './topicPickerHelpers';

const _byParent = (topics) => {
  const map = new Map();
  topics.forEach((t) => {
    const parent = t.parent_topic_id || null;
    if (!map.has(parent)) map.set(parent, []);
    map.get(parent).push(t);
  });
  return map;
};

const _breadcrumb = (topics, topicId) => {
  const byId = new Map(topics.map((t) => [t.id, t]));
  const parts = [];
  let cur = byId.get(topicId);
  let hops = 0;
  while (cur && hops < 20) {
    parts.unshift(cur.name);
    cur = cur.parent_topic_id ? byId.get(cur.parent_topic_id) : null;
    hops += 1;
  }
  return parts;
};

// Return the set of ancestors for `topicId` (excluding itself).
const _ancestors = (topics, topicId) => {
  const byId = new Map(topics.map((t) => [t.id, t]));
  const out = [];
  let cur = byId.get(topicId);
  let hops = 0;
  while (cur && cur.parent_topic_id && hops < 20) {
    out.push(cur.parent_topic_id);
    cur = byId.get(cur.parent_topic_id);
    hops += 1;
  }
  return out;
};

// Find pairs (ancestor, descendant) where both are selected. Redundant
// tags to hint at.
const _findRedundancies = (topics, selectedIds) => {
  const selectedSet = new Set(selectedIds);
  const redundantAncestors = new Set();
  selectedIds.forEach((id) => {
    _ancestors(topics, id).forEach((aId) => {
      if (selectedSet.has(aId)) redundantAncestors.add(aId);
    });
  });
  return redundantAncestors;
};

const TreeNode = ({ node, byParent, depth, expanded, onToggle, onToggleCheck, selectedSet, testIdPrefix }) => {
  const children = byParent.get(node.id) || [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(node.id);
  const isChecked = selectedSet.has(node.id);

  return (
    <div className="w-full">
      <div
        className={`w-full flex items-start gap-1.5 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors ${
          isChecked ? 'bg-[#0A7EA4]/5' : ''
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-slate-200"
            data-testid={`${testIdPrefix}-toggle-${node.id}`}
          >
            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span className="mt-0.5 flex-shrink-0 w-4 h-4" />
        )}
        <label
          className="flex-1 min-w-0 flex items-start gap-2 cursor-pointer"
          data-testid={`${testIdPrefix}-row-${node.id}`}
        >
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => onToggleCheck(node.id)}
            className="mt-1 flex-shrink-0 accent-[#0A7EA4]"
            data-testid={`${testIdPrefix}-check-${node.id}`}
          />
          <span
            className={`min-w-0 break-words text-sm leading-snug line-clamp-2 ${
              isChecked ? 'font-medium text-slate-900' : 'text-slate-700'
            }`}
          >
            {node.name}
          </span>
        </label>
      </div>
      {hasChildren && isOpen && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              byParent={byParent}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onToggleCheck={onToggleCheck}
              selectedSet={selectedSet}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const TopicPickerMulti = ({
  topics = [],
  value = [],
  onChange,
  placeholder = 'Tag topics…',
  disabled = false,
  className = '',
  testIdPrefix = 'topic-picker-multi',
  // When true (admin/tagging surfaces), highlights redundant ancestor
  // tags and offers a one-click Fix. When false (read-only/filter
  // surfaces like Practice Sessions), users are picking filters, not
  // persisting tags — descendant expansion already collapses the set
  // server-side, so there's nothing to "fix". Chips also render in the
  // neutral style regardless of ancestry.
  showRedundancyHint = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  // Focused mode: when set, the picker is "drilled into" the subtree
  // rooted at this topic id. Search filters within the subtree, and the
  // browse-tree renders that node's children as the new roots. Set by
  // clicking the "explore inside" chevron on a search result. Cleared
  // by the Back button in the header pill.
  const [focusRootId, setFocusRootId] = useState(null);
  const rootRef = useRef(null);

  const byParent = useMemo(() => _byParent(topics), [topics]);
  const roots = byParent.get(null) || [];
  const selectedSet = useMemo(() => new Set(value || []), [value]);
  // Effective roots the tree browser paints. Normally the tree's real
  // roots; in Focused mode, the children of the focus root — so the
  // picker looks like a smaller, subtree-scoped picker.
  const effectiveRoots = useMemo(
    () => (focusRootId ? (byParent.get(focusRootId) || []) : roots),
    [focusRootId, byParent, roots]
  );
  // Set of ids the search is allowed to match in Focused mode. Includes
  // the focus root itself so admins can still pick the branch parent.
  const focusScopeIds = useMemo(
    () => (focusRootId ? getSubtreeIds(byParent, focusRootId) : null),
    [focusRootId, byParent]
  );
  // Breadcrumb parts for the focused root, for the header pill.
  const focusPath = useMemo(
    () => (focusRootId ? _breadcrumb(topics, focusRootId) : []),
    [focusRootId, topics]
  );
  const redundantAncestors = useMemo(
    () =>
      showRedundancyHint
        ? _findRedundancies(topics, Array.from(selectedSet))
        : new Set(),
    [topics, selectedSet, showRedundancyHint]
  );

  // Auto-expand ancestors of currently-selected nodes when opening.
  useEffect(() => {
    if (!open || selectedSet.size === 0) return;
    const byId = new Map(topics.map((t) => [t.id, t]));
    const toOpen = new Set(expanded);
    selectedSet.forEach((id) => {
      let cur = byId.get(id);
      let hops = 0;
      while (cur && cur.parent_topic_id && hops < 20) {
        toOpen.add(cur.parent_topic_id);
        cur = byId.get(cur.parent_topic_id);
        hops += 1;
      }
    });
    setExpanded(toOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleNode = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCheck = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.(Array.from(next));
  };

  const removeChip = (id) => {
    const next = new Set(selectedSet);
    next.delete(id);
    onChange?.(Array.from(next));
  };

  const fixRedundancies = () => {
    const next = new Set(selectedSet);
    redundantAncestors.forEach((id) => next.delete(id));
    onChange?.(Array.from(next));
  };

  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return topics
      .filter((t) => {
        if (focusScopeIds && !focusScopeIds.has(t.id)) return false;
        return t.name.toLowerCase().includes(q);
      })
      .map((t) => ({
        ...t,
        path: _breadcrumb(topics, t.id),
        // Descendant count — used both for the "· N inside" hint and
        // to decide whether to render the drill-in chevron.
        childCount: countDescendants(byParent, t.id),
      }))
      .slice(0, 80);
  }, [query, topics, focusScopeIds, byParent]);

  const chips = useMemo(
    () =>
      Array.from(selectedSet).map((id) => ({
        id,
        path: _breadcrumb(topics, id),
        redundant: redundantAncestors.has(id),
      })),
    [selectedSet, topics, redundantAncestors]
  );

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={`${testIdPrefix}-root`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[38px] px-3 py-2 text-left border border-slate-300 rounded-md bg-white hover:border-[#0A7EA4] focus:outline-none focus:ring-2 focus:ring-[#0A7EA4]/30 transition-colors disabled:bg-slate-50 disabled:cursor-not-allowed"
        data-testid={`${testIdPrefix}-trigger`}
      >
        {chips.length === 0 ? (
          <span className="text-sm text-slate-400">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip.id}
                className={`inline-flex items-start gap-1 px-2 py-0.5 rounded text-xs max-w-full ${
                  chip.redundant
                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                    : 'bg-[#0A7EA4]/10 text-[#0A7EA4]'
                }`}
                data-testid={`${testIdPrefix}-chip-${chip.id}`}
              >
                <span className="break-words line-clamp-2 min-w-0 leading-snug">
                  {chip.path.map((seg, idx) => (
                    <span key={idx}>
                      {idx > 0 && <span className="mx-0.5 opacity-60">›</span>}
                      {seg}
                    </span>
                  ))}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChip(chip.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      removeChip(chip.id);
                    }
                  }}
                  className="mt-0.5 flex-shrink-0 hover:opacity-70 cursor-pointer"
                  data-testid={`${testIdPrefix}-chip-remove-${chip.id}`}
                >
                  <X className="w-3 h-3" />
                </span>
              </span>
            ))}
          </div>
        )}
      </button>

      {open && (
        <div
          className="absolute z-20 mt-1 w-full min-w-[320px] bg-white border border-slate-300 rounded-md shadow-lg max-h-[400px] flex flex-col"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {redundantAncestors.size > 0 && (
            <div
              className="flex items-start gap-2 p-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-900"
              data-testid={`${testIdPrefix}-redundancy-hint`}
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium">
                  {redundantAncestors.size === 1 ? '1 tag is redundant' : `${redundantAncestors.size} tags are redundant`}
                </p>
                <p className="text-amber-800 mt-0.5 leading-relaxed">
                  Filtering by any ancestor already surfaces this question via its deepest tag. Removing the redundant tag(s) keeps the tag set clean.
                </p>
              </div>
              <button
                type="button"
                onClick={fixRedundancies}
                className="flex-shrink-0 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded transition-colors"
                data-testid={`${testIdPrefix}-fix-redundancy`}
              >
                Fix
              </button>
            </div>
          )}

          {focusRootId && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs"
              data-testid={`${testIdPrefix}-focus-pill`}
            >
              <button
                type="button"
                onClick={() => { setFocusRootId(null); setExpanded(new Set()); }}
                className="flex-shrink-0 inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                data-testid={`${testIdPrefix}-focus-back`}
                title="Return to full topic list"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 flex-shrink-0">Inside:</span>
              <span className="min-w-0 break-words leading-snug text-slate-800">
                {focusPath.map((seg, idx) => (
                  <span key={idx}>
                    {idx > 0 && <span className="mx-1 text-slate-400">›</span>}
                    <span className={idx === focusPath.length - 1 ? 'font-medium' : ''}>{seg}</span>
                  </span>
                ))}
              </span>
            </div>
          )}

          <div className="p-2 border-b border-slate-200 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={focusRootId ? 'Search inside subtree…' : 'Search topics…'}
              className="flex-1 min-w-0 text-sm outline-none placeholder:text-slate-400"
              data-testid={`${testIdPrefix}-search`}
              autoFocus
            />
            {selectedSet.size > 0 && (
              <button
                type="button"
                onClick={() => onChange?.([])}
                className="flex-shrink-0 text-xs text-slate-500 hover:text-slate-700 px-2 py-0.5 rounded hover:bg-slate-100"
                data-testid={`${testIdPrefix}-clear-all`}
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-1">
            {searchResults ? (
              searchResults.length === 0 ? (
                <div className="p-3 text-sm text-slate-500 text-center">
                  No topics match "{query}"
                </div>
              ) : (
                searchResults.map((t) => {
                  const isChecked = selectedSet.has(t.id);
                  const hasChildren = t.childCount > 0;
                  return (
                    <div
                      key={t.id}
                      className={`flex items-start gap-1 px-2 py-1.5 rounded-md hover:bg-slate-100 ${
                        isChecked ? 'bg-[#0A7EA4]/5' : ''
                      }`}
                      data-testid={`${testIdPrefix}-search-result-${t.id}`}
                    >
                      <label className="flex-1 min-w-0 flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleCheck(t.id)}
                          className="mt-1 flex-shrink-0 accent-[#0A7EA4]"
                        />
                        <span className="block text-sm text-slate-700 break-words leading-snug min-w-0 flex-1">
                          {t.path.map((seg, idx) => (
                            <span key={idx}>
                              {idx > 0 && <span className="mx-1 text-slate-400">›</span>}
                              <span className={idx === t.path.length - 1 ? 'font-medium text-slate-900' : ''}>
                                {seg}
                              </span>
                            </span>
                          ))}
                          {hasChildren && (
                            <span className="ml-1.5 text-[10px] text-slate-400 whitespace-nowrap">
                              · {t.childCount} inside
                            </span>
                          )}
                        </span>
                      </label>
                      {hasChildren && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFocusRootId(t.id);
                            setQuery('');
                            setExpanded(new Set());
                          }}
                          className="flex-shrink-0 mt-0.5 p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200"
                          data-testid={`${testIdPrefix}-drill-${t.id}`}
                          title={`Explore inside ${t.name}`}
                        >
                          <FolderTree className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })
              )
            ) : effectiveRoots.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 text-center">
                {focusRootId ? 'No subtopics inside this branch.' : 'No topics available'}
              </div>
            ) : (
              effectiveRoots.map((root) => (
                <TreeNode
                  key={root.id}
                  node={root}
                  byParent={byParent}
                  depth={0}
                  expanded={expanded}
                  onToggle={toggleNode}
                  onToggleCheck={toggleCheck}
                  selectedSet={selectedSet}
                  testIdPrefix={testIdPrefix}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicPickerMulti;
